import { useTranslation } from 'react-i18next';
import { EyeOff, HandHelping, KeyRound, Pause, TriangleAlert } from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  type AssistRequestStatus,
  type DeviceDto,
  type DeviceStatus,
} from '@gc/shared';

import { DeviceArtwork } from './DeviceArtwork';
import { DeviceActionsMenu } from './ActionsMenu';
import type { DeviceMenuItemId } from './deviceMenu';
import { launcherNoticeLabel, launcherNoticesOf } from './launcherNotices';

/**
 * Status lamp colour (approved board card: an 8px dot that GLOWS its state).
 * Green reads "free", status blue reads "in use" — matching what staff already
 * recognise from the incumbent system.
 */
const STATUS_DOT: Record<DeviceStatus, string> = {
  AVAILABLE: 'bg-available shadow-[var(--glow-available)]',
  // Busy is the status BLUE, not the accent cyan (spec: a busy tile and a
  // selected control must be distinguishable at a glance).
  BUSY: 'bg-busy shadow-[var(--glow-busy)]',
  ABOUT_TO_END: 'bg-ending shadow-[var(--glow-ending)]',
  OUT_OF_SERVICE: 'bg-danger shadow-[var(--glow-danger)]',
};

/**
 * The tile edge carries the state (spec §5): light as information. Paused
 * overrides with a dashed neutral edge — a held machine is deliberately
 * de-energised.
 *
 * A RESERVED machine is ENERGISED, not merely outlined (owner request: "make
 * it more clear whenever a device is reserved"). The busy tile glows blue
 * outward and breathes the same blue inside the card, so an occupied machine
 * reads as a lit instrument from across the counter — the border hue alone
 * vanished on a 28-tile board. Ending-soon is the same treatment in amber;
 * a free tile stays dark, which is exactly what makes the lit ones read.
 */
const STATE_EDGE: Record<DeviceStatus, string> = {
  AVAILABLE: 'border-[rgba(52,211,153,0.35)]',
  // Near-solid edge, a wide halo and a deep interior wash — bold enough to
  // count occupied machines from the door, tuned up once at owner request.
  BUSY: 'border-[rgba(96,165,250,0.85)] shadow-[0_0_18px_rgba(96,165,250,0.5),inset_0_0_64px_rgba(96,165,250,0.17)]',
  ABOUT_TO_END:
    'border-[rgba(251,191,36,0.85)] shadow-[0_0_18px_rgba(251,191,36,0.5),inset_0_0_64px_rgba(251,191,36,0.17)]',
  OUT_OF_SERVICE: 'border-[rgba(244,63,94,0.45)]',
};

/**
 * The lit colour SAYS WHAT KIND of occupation this is (owner request): blue
 * is a paying sitting, VIOLET is maintenance behind the support unlock.
 * Violet rides the venue's own secondary accent.
 *
 * A free-MODE sitting is deliberately NOT a kind of its own any more
 * (owner, 2026-08-11: "i want it to say Reserved"). The customer roams the
 * Windows desktop instead of the portal, but to the board it is a paid,
 * reserved machine like any other — the green treatment and the "Free mode"
 * badge made staff read an ordinary sitting as something special.
 */
const SUPPORT_EDGE =
  'border-[color-mix(in_srgb,var(--brand-secondary)_85%,transparent)] shadow-[0_0_18px_color-mix(in_srgb,var(--brand-secondary)_50%,transparent),inset_0_0_64px_color-mix(in_srgb,var(--brand-secondary)_17%,transparent)]';

/** Seconds as the card's timer format: H:MM:SS, hours unpadded. */
function clock(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** Elapsed time as H:MM:SS — a live second hand, exactly as the card draws it. */
function elapsed(startedAt: string, now: number): string {
  return clock(Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000)));
}

/**
 * The portal's countdown, on the staff tile (owner request): time LEFT as
 * H:MM:SS, ticking down between polls. `secondsRemaining` is as-of the
 * snapshot at `receivedAt`; the drift since then is local. Frozen while paused
 * — the server folds the pause, and ticking locally would count down time the
 * customer is not using. Exported for its test.
 */
export function remaining(
  secondsRemaining: number,
  receivedAt: number,
  now: number,
  paused: boolean,
): string {
  const drift = paused ? 0 : Math.max(0, Math.floor((now - receivedAt) / 1000));
  return clock(Math.max(0, secondsRemaining - drift));
}

/** Whole minutes since an instant, floored at zero (clock skew, fresh pauses). */
function minutesSince(iso: string, now: number): number {
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000));
}

/** Local wall-clock HH:mm for the "Reserved 18:00" ribbon. */
function reservedTime(iso: string): string {
  const date = new Date(iso);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

export type TileAction = 'reserve' | 'orders' | 'stopAndClose' | 'details';

interface DeviceTileProps {
  device: DeviceDto;
  now: number;
  /** When the board snapshot arrived — the countdown ticks from here. */
  receivedAt?: number;
  currencySymbol: string;
  selected: boolean;
  /**
   * `enableReservationAlerts` — gates the ATTENTION surface only (the amber
   * ribbon here, the banner and soft confirm on the page). The `nextBooking`
   * data itself is always embedded (WAVE2-DESIGN §1).
   */
  reservationAlertsEnabled?: boolean;
  /**
   * The device's open assist request, when one exists (wave 3 #13) —
   * renders the raised-hand badge over the card.
   */
  assistStatus?: AssistRequestStatus | null;
  /**
   * Permission check for the Actions menu, injected rather than read from
   * context so the tile stays renderable with a bare AuthProvider in tests.
   * Defaults to "nothing granted", which still leaves the per-session items.
   */
  can?: (permission: string) => boolean;
  onToggleSelect: (device: DeviceDto) => void;
  onAction: (device: DeviceDto, action: TileAction) => void;
  /** A pick from the 3-dot Actions menu (wave 3 #5). */
  onMenuAction?: (device: DeviceDto, item: DeviceMenuItemId) => void;
  /**
   * Done on a launcher notice (owner, 2026-10-05): staff have signed the
   * venue account back into that launcher on this PC.
   */
  onLauncherNoticeDone?: (device: DeviceDto, auditId: string) => void;
}

const CAN_NOTHING = () => false;

/** The floating state flag riding the tile's top edge (card `.fbadge`). */
const FLAG_BASE =
  'absolute -top-[9px] z-20 truncate rounded-full border bg-overlay px-2 py-0.5 ' +
  'text-[9px] uppercase tracking-[0.12em]';

export function DeviceTile({
  device,
  now,
  receivedAt = 0,
  currencySymbol,
  selected,
  reservationAlertsEnabled = false,
  assistStatus = null,
  can = CAN_NOTHING,
  onToggleSelect,
  onAction,
  onMenuAction,
  onLauncherNoticeDone,
}: DeviceTileProps) {
  const { t } = useTranslation();
  const session = device.currentSession;

  /**
   * The support-mode window, alive only while its deadline is ahead of the
   * board clock — `now` ticks every second, so the violet dies on time even
   * between polls. Never while a session runs; a sitting outranks it.
   */
  const supportUntil = device.supportModeUntil ? new Date(device.supportModeUntil).getTime() : null;
  const supportActive = !session && supportUntil !== null && supportUntil > now;

  /**
   * The lamp. Paused keeps the busy BLUE — a held machine still counts in the
   * busy total staff scan the board for — but surrenders its glow, because
   * nothing on a held tile is allowed to radiate (approved card: only the
   * `.busy` state lights its lamp). Offline goes slate: absence of light.
   * Support mode carries its tile's own current.
   */
  const lamp = session?.paused
    ? 'bg-busy'
    : device.isOffline
      ? 'bg-offline'
      : supportActive
        ? 'bg-secondary shadow-[0_0_10px_color-mix(in_srgb,var(--brand-secondary)_35%,transparent)]'
        : STATUS_DOT[device.status];

  /** The tile's lit state, in precedence order — see the edge constants. */
  const edge = session?.paused
    ? // Paused wins the edge: a held machine is deliberately de-energised,
      // whatever colour its sitting would otherwise carry.
      'border-dashed border-strong shadow-none'
    : device.status === 'ABOUT_TO_END'
      ? STATE_EDGE.ABOUT_TO_END
      : session
        ? STATE_EDGE.BUSY
        : supportActive
          ? SUPPORT_EDGE
          : STATE_EDGE[device.status];

  return (
    <div className="relative flex h-full flex-col">
      {/*
        A hidden device is only on screen because someone ticked "show hidden".
        Saying so on the tile stops it being mistaken for a machine that has
        quietly come back onto the board.
      */}
      {device.isHidden ? (
        <span
          data-hidden-badge
          className="absolute start-2 top-2 z-20 inline-flex items-center gap-1 rounded-full bg-surface/90 px-2 py-0.5 text-[11px] text-content-muted ring-1 ring-subtle"
        >
          <EyeOff className="h-3 w-3" aria-hidden />
          {t('devices.hidden')}
        </span>
      ) : null}

      {/*
        The state flag over the top edge (approved card): the two states a
        manager must catch from across the room get words, not just colour.
        Paused wins — a held sitting that is also about to end is above all
        NOT BILLING, and the whole point of the flag is that nothing moves.
      */}
      {session?.paused ? (
        <span
          data-paused="true"
          title={t('deviceBoard.paused.badge', { name: session.tabName })}
          className={`${FLAG_BASE} border-strong text-content-secondary`}
          style={{ insetInlineStart: '10px', maxWidth: 'calc(100% - 20px)' }}
        >
          {t('deviceBoard.paused.noClock')}
        </span>
      ) : device.status === 'ABOUT_TO_END' ? (
        <span
          data-state-badge="ending"
          className={`${FLAG_BASE} border-[rgba(251,191,36,0.5)] text-ending`}
          style={{ insetInlineStart: '10px', maxWidth: 'calc(100% - 20px)' }}
        >
          {t('deviceBoard.badge.endingSoon')}
        </span>
      ) : session?.complimentary ? (
        /* The Play button's courtesy sitting (owner, 2026-08-12): "Play
           Mode", in green — "Reserved" would send staff hunting for money
           nobody owes. Green is honest here precisely because this one
           genuinely IS free, unlike the paid free-mode sitting below. */
        <span
          data-state-badge="playMode"
          className={`${FLAG_BASE} border-[rgba(52,211,153,0.5)] text-available`}
          style={{ insetInlineStart: '10px', maxWidth: 'calc(100% - 20px)' }}
        >
          {t('deviceBoard.badge.playMode')}
        </span>
      ) : session ? (
        /* A plain paying sitting says so too (owner request): every occupied
           state now carries words over the top edge, not just the odd ones.
           FREE-MODE SITTINGS LAND HERE ON PURPOSE (owner, 2026-08-11): the
           machine roams Windows, but the board calls it what the money says
           it is — Reserved. */
        <span
          data-state-badge="reserved"
          className={`${FLAG_BASE} border-[rgba(96,165,250,0.5)] text-busy`}
          style={{ insetInlineStart: '10px', maxWidth: 'calc(100% - 20px)' }}
        >
          {t('deviceBoard.badge.reserved')}
        </span>
      ) : supportActive ? (
        <span
          data-state-badge="support"
          className={`${FLAG_BASE} border-secondary/50 text-secondary`}
          style={{ insetInlineStart: '10px', maxWidth: 'calc(100% - 20px)' }}
        >
          {t('deviceBoard.badge.supportMode')}
        </span>
      ) : null}

      {/* Raised hand (wave 3 #13): a customer at this machine wants a human. */}
      {assistStatus ? (
        <span
          data-assist-badge={assistStatus}
          title={t('deviceBoard.assist.badge', { device: device.name })}
          className={[
            'absolute z-20 grid h-7 w-7 place-items-center rounded-full text-white shadow-lg',
            assistStatus === 'ACKED' ? 'bg-primary' : 'animate-pulse bg-ending',
          ].join(' ')}
          style={{ insetInlineEnd: '0.25rem', top: '-0.5rem' }}
        >
          <HandHelping className="h-4 w-4" aria-hidden />
        </span>
      ) : null}

      <div
        className={[
          'flex flex-1 flex-col rounded-card border bg-raised px-3 pb-3 pt-2.5',
          edge,
          // An offline agent dims the whole card — offline is the absence of
          // light (spec §2.1); the artwork greys out with it below.
          device.isOffline ? 'opacity-[0.78]' : '',
        ].join(' ')}
      >
        {reservationAlertsEnabled && device.nextBooking ? (
          <div
            data-ribbon="reserved"
            className="-mx-3 -mt-2.5 mb-2 truncate rounded-t-card bg-ending/20 px-2 py-0.5 text-center text-[11px] font-medium text-ending"
            title={`${reservedTime(device.nextBooking.startsAt)} — ${device.nextBooking.partyName}`}
          >
            {t('bookings.tile.reserved', {
              time: reservedTime(device.nextBooking.startsAt),
              name: device.nextBooking.partyName,
            })}
          </div>
        ) : null}
        {/*
          ZONE 1 — the header (card `.z-head`): checkbox, name, the category as
          a 9px overline, then the lamp pushed to the end and the 3-dot menu.
          The menu sits up here rather than in the action row so the two things
          staff press all shift — reserve and order — get that row to
          themselves.
        */}
        <div className="flex items-center gap-[7px]">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(device)}
            aria-label={t('devices.selectDevice', { device: device.name })}
            className="h-3.5 w-3.5 shrink-0 rounded-sm accent-[var(--brand-primary)]"
          />
          {/*
            The name opens the panel: a free machine to start it, a busy one
            just to look — who is on it, how long, what it has run up. It used
            to open the party's checkout drawer, which put a payment screen one
            stray tap away on a board of forty tiles.
          */}
          <button
            type="button"
            onClick={() => onAction(device, session ? 'details' : 'reserve')}
            className="min-w-0 truncate text-start text-[13px] font-semibold text-content hover:text-primary max-md:min-h-10"
            title={device.name}
          >
            {device.name}
          </button>
          <span className="max-w-24 shrink-0 truncate text-[9px] uppercase tracking-[0.16em] text-content-muted">
            {device.categoryName}
          </span>
          <span
            aria-label={t(`devices.status.${statusKey(device.status)}`)}
            title={t(`devices.status.${statusKey(device.status)}`)}
            className={`ms-auto h-2 w-2 shrink-0 rounded-full ${lamp}`}
          />
          <DeviceActionsMenu
            device={device}
            currencySymbol={currencySymbol}
            can={can}
            onSelect={(menuDevice, item) => onMenuAction?.(menuDevice, item)}
          />
        </div>

        {/*
          ZONE 2 — THE INSTRUMENT (card `.z-inst`): the timer is the biggest
          thing on a busy tile, 19px of numerals above the artwork by
          construction, with the running cost at its side. A free tile shows
          the rate here; an out-of-service one, the reason.
        */}
        <div className="mt-2 flex min-h-[26px] items-baseline justify-between gap-2">
          {session ? (
            <>
              <span
                className={[
                  'tabular text-[19px] leading-none font-semibold',
                  // Paused goes quiet; ending glows amber; a paying one —
                  // free-mode included — counts in blue.
                  session.paused
                    ? 'text-content-secondary'
                    : device.status === 'ABOUT_TO_END'
                      ? 'text-ending [text-shadow:var(--glow-ending)]'
                      : 'text-busy',
                ].join(' ')}
              >
                {/* An account sitting shows the portal's own countdown — what
                    the CUSTOMER has left — instead of wall-clock elapsed. */}
                {session.secondsRemaining !== null
                  ? remaining(session.secondsRemaining, receivedAt, now, session.paused)
                  : elapsed(session.startedAt, now)}
              </span>
              <span className="tabular shrink-0 text-xs font-semibold text-content">
                {formatIqd(fromMillidinars(session.runningCostMd), { currencySymbol })}
              </span>
            </>
          ) : device.status === 'OUT_OF_SERVICE' ? (
            <span className="text-[11px] text-content-muted">
              {device.outOfServiceReason || t('devices.status.outOfService')}
            </span>
          ) : (
            <span className="text-[11px] text-content-muted">
              {device.reservationType === 'HOURLY'
                ? `${formatIqd(fromMillidinars(device.hourlyPriceMd), { currencySymbol })} / ${t('devices.perHour')}`
                : `${formatIqd(fromMillidinars(device.singleGamePriceMd), { currencySymbol })} / ${t('devices.perRound')}`}
            </span>
          )}
        </div>

        {/* ZONE 3 — context: who is on it, the pause explained, or "Available". */}
        <div className="mt-0.5 min-h-4 text-[11px] text-content-secondary">
          {session ? (
            session.paused ? (
              /* The cost above is FROZEN server-side while paused. A number
                 that does not move with no reason given reads as a broken
                 tile, so the pause is said out loud — with how long. */
              <div data-paused-label="" className="flex items-center gap-1 font-medium">
                <Pause className="h-3 w-3 shrink-0 fill-current" aria-hidden />
                <span className="truncate">
                  {session.pausedAt
                    ? t('deviceBoard.paused.forMinutes', {
                        minutes: minutesSince(session.pausedAt, now),
                      })
                    : t('deviceBoard.paused.noClock')}
                </span>
              </div>
            ) : (
              <div className="truncate">{session.tabName}</div>
            )
          ) : device.status === 'AVAILABLE' && !supportActive ? (
            // A machine behind a support unlock is NOT for sale even though
            // its status says available — the flag above says what it is.
            t('devices.status.available')
          ) : null}
        </div>

        {/*
          ZONE 4 — the artwork (card `.z-art`): a fixed 88px stage at 100%
          zoom, the machine centred on it at up to 70% of the tile's width,
          floating on its own drop shadow. In normal flow below the numbers —
          the zone order the owner approved.
        */}
        <div
          // FLEX, not grid, and that is load-bearing: the artwork is an <img>
          // whose max-height is a PERCENTAGE, and in a grid with auto rows
          // that percentage resolves against the content-sized track — the
          // picture laid out at its natural height and the zone clipped its
          // bottom half, which read as "the artwork is hidden behind the
          // buttons". A flex container resolves it against this box's own
          // definite height, so the machine scales INTO its 88px stage.
          className="pointer-events-none relative mt-1 flex w-full items-center justify-center overflow-hidden"
          // Scales with the board's zoom control, as before. 7.5rem, not the
          // card's 88px: the venue's renders are the tile's face (owner: "i
          // want the artwork to stay") and at 88px they read as thumbnails.
          style={{ height: 'calc(var(--tile-scale, 1) * 7.5rem)' }}
        >
          {device.imageUrl ? (
            // A venue-uploaded photo shows exactly as shot — no scale-up,
            // which would crop real pixels.
            <img
              src={device.imageUrl}
              alt=""
              className={`max-h-full max-w-[92%] object-contain drop-shadow-[0_6px_12px_rgba(0,0,0,0.6)] ${device.isOffline ? 'opacity-50 grayscale' : ''}`}
              loading="lazy"
            />
          ) : (
            // The built-in renders carry generous transparent margins baked
            // in (they were made to float over the old tile), so a modest
            // scale-up crops only that padding and lets the machine itself
            // fill its stage.
            <DeviceArtwork
              deviceType={device.deviceType}
              categoryName={device.categoryName}
              className={`max-h-full w-auto max-w-full scale-[1.25] object-contain text-content-secondary/70 drop-shadow-[0_6px_12px_rgba(0,0,0,0.6)] ${device.isOffline ? 'opacity-50 grayscale' : ''}`}
            />
          )}
        </div>

        {/*
          WHAT THE MACHINE ITSELF SAYS WENT WRONG.
          Sits under the status line, below whatever the session is doing,
          because it is about the PC rather than the sitting — a launch can be
          refused on a machine that is otherwise perfectly happy.

          It exists because a launch that quietly does nothing is the failure
          staff cannot diagnose: the API answers 201 the moment it pushes the
          frame, so the portal looks like it worked while the agent's reason
          ("unknown_id" for a game added minutes ago) went nowhere. Reported
          live over the socket, so a PC that recovers drops the badge on its
          next heartbeat rather than wearing yesterday's fault.
        */}
        {device.agentError ? (
          <p
            data-agent-error=""
            title={device.agentError}
            className="mt-1 flex items-start gap-1 text-[11px] leading-snug font-medium text-danger"
          >
            <TriangleAlert className="mt-px h-3 w-3 shrink-0" aria-hidden />
            <span className="line-clamp-2">{device.agentError}</span>
          </p>
        ) : null}

        {/*
          LAUNCHERS A CUSTOMER SIGNED THEIR OWN ACCOUNT INTO (owner,
          2026-10-05). "Login using my account" on an Epic, Riot, Ubisoft…
          game opens that launcher for the customer to sign the venue account
          out and their own in, and nothing on the PC puts it back — the next
          customer's Play would start on a stranger's account. So the tile
          says so, in amber, until someone has signed the venue account back
          in and pressed Done. Like the agent's error above, it is about the
          PC rather than the sitting. Steam shows here too (owner,
          2026-10-19): after the sitting Steam stays signed in to the
          customer's account until the next Play on a Steam game with a
          venue account switches it back (the lock closes Steam's game but
          never touches its account), so staff are warned the same way, with
          no automatic reset. The server sends it, and nothing here filters a
          launcher out.

          STEAM HAS ITS OWN SENTENCE (review, U4 rounds 2 and 4). A Free Mode
          customer who opens Steam meanwhile gets that account. With Remember
          me ticked it also stays in Steam's Switch account list, and can sign
          itself in again when the PC restarts. So staff sign the venue
          account back in, then remove the customer's from that list, in that
          order (the pilot's): the shared sentence named only the sign-in.
        */}
        {launcherNoticesOf(device).map((notice) => {
          // The whole sentence on hover too, as the agent's error above has
          // it: a long Arabic or Kurdish line can clip its second half, the
          // part that says what to do, on a narrow tile.
          const message =
            notice.family === 'steam'
              ? t('deviceBoard.launcherNotice.messageSteam')
              : t('deviceBoard.launcherNotice.message', {
                  launcher: launcherNoticeLabel(notice),
                });
          return (
            <div
              key={notice.auditId}
              data-launcher-notice={notice.family}
              className="mt-1 flex items-start gap-1.5 rounded-control bg-ending/15 px-2 py-1 text-[11px] leading-snug font-medium text-ending"
            >
              <KeyRound className="mt-px h-3 w-3 shrink-0" aria-hidden />
              <span title={message} className="line-clamp-3 min-w-0 flex-1">
                {message}
              </span>
              <button
                type="button"
                data-launcher-notice-done={notice.auditId}
                onClick={() => onLauncherNoticeDone?.(device, notice.auditId)}
                className="shrink-0 rounded-full border border-[rgba(251,191,36,0.5)] px-2 py-0.5 text-[10.5px] font-semibold text-ending hover:bg-ending/20 max-md:min-h-10"
              >
                {t('deviceBoard.launcherNotice.done')}
              </button>
            </div>
          );
        })}

        {/*
          ZONE 5 — the actions (card `.z-act`), bottom-anchored so every tile
          in a row ends on the same line: two 30px pills as equal halves, so
          neither is the one you have to hunt for. Only a FREE tile carries a
          filled button — on a busy tile nothing on the face is "primary",
          exactly as the card draws it. Everything else — pairing, edit,
          out-of-service — stays behind the 3-dot menu, because five buttons
          per card made a 40-machine board a wall of identical circles.

          The busy half STOPS THE SESSION. It used to open the party's checkout
          drawer while calling itself "Stop & close", so the one button staff
          press to free a machine left the clock running and the customer still
          being billed. Taking the money is the Outstanding screen's job.
        */}
        <div className="mt-auto flex items-stretch gap-2 pt-2.5">
          <TileButton
            primary={!session}
            label={session ? t('sessions.stopAndClose') : t('sessions.reserve')}
            onClick={() => onAction(device, session ? 'stopAndClose' : 'reserve')}
            disabled={device.status === 'OUT_OF_SERVICE'}
          />
          <TileButton
            label={t('devices.actions.addOrder')}
            onClick={() => onAction(device, 'orders')}
            disabled={device.status === 'OUT_OF_SERVICE'}
          />
        </div>
      </div>
    </div>
  );
}

function TileButton({
  label,
  onClick,
  primary,
  disabled,
}: {
  label: string;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={[
        // Half the row each, text-only per the card — the icons went with the
        // circles. `min-w-0` lets the label truncate at the smaller zoom steps
        // instead of pushing the buttons out of the card.
        'flex min-w-0 flex-1 items-center justify-center rounded-full px-2',
        'h-[30px] text-[11.5px] font-semibold transition-colors',
        primary
          ? // `no-glow`: the card's small filled pill does not radiate — the
            // shell-level glow rule is for a screen's ONE primary action.
            'no-glow bg-primary text-primary-fg hover:bg-primary-hover'
          : 'border border-strong text-content hover:bg-hover',
        disabled ? 'cursor-not-allowed opacity-35 hover:bg-transparent' : '',
      ].join(' ')}
    >
      <span className="truncate">{label}</span>
    </button>
  );
}

function statusKey(status: DeviceStatus): string {
  return {
    AVAILABLE: 'available',
    BUSY: 'busy',
    ABOUT_TO_END: 'aboutToEnd',
    OUT_OF_SERVICE: 'outOfService',
  }[status];
}
